import { formatRelativeTime } from "@/lib/utils/formatting";

interface TimelineEvent {
  id: string;
  title: string;
  timestamp?: string;
  description?: string;
  status?: string;
}

// Color mapping aligned with StatusBadge status values
const STATUS_DOT_COLORS: Record<string, string> = {
  active: "var(--ok)",
  healthy: "var(--ok)",
  succeeded: "var(--ok)",
  approved: "var(--ok)",
  applied: "var(--ok)",
  error: "var(--danger)",
  failed: "var(--danger)",
  rejected: "var(--danger)",
  paused: "var(--warn)",
  degraded: "var(--warn)",
  pending: "var(--accent)",
  running: "var(--accent)",
  draft: "var(--muted-foreground)",
  canceled: "var(--muted-foreground)",
  expired: "var(--muted-foreground)",
};

function dotColor(status?: string): string {
  if (!status) {return "var(--muted-foreground)";}
  return STATUS_DOT_COLORS[status] ?? "var(--muted-foreground)";
}

export function Timeline({ events }: { events: TimelineEvent[] }) {
  if (events.length === 0) {return null;}

  return (
    <div className="flex flex-col">
      {events.map((event, i) => {
        const isLast = i === events.length - 1;
        return (
          <div key={event.id} className="flex gap-3 relative">
            {/* Dot and line column */}
            <div className="flex flex-col items-center w-3 shrink-0">
              <div
                className="w-2.5 h-2.5 rounded-full mt-1 shrink-0 border-2"
                style={{
                  borderColor: dotColor(event.status),
                  background: dotColor(event.status),
                }}
              />
              {!isLast && (
                <div
                  className="w-px flex-1 min-h-4"
                  style={{ background: "var(--border)" }}
                />
              )}
            </div>

            {/* Content */}
            <div className={isLast ? "pb-0" : "pb-4"}>
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
                  {event.title}
                </span>
                {event.timestamp && (
                  <span className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>
                    {formatRelativeTime(event.timestamp)}
                  </span>
                )}
              </div>
              {event.description && (
                <p className="text-xs mt-0.5 leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
                  {event.description}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
